// lib/auth.js — Phase 33 + 35c: Client-side auth context.
//
// Phase 35c: Local-first auth model. Every visitor is a "local user"
// (uuid in localStorage / IndexedDB). They can optionally connect a
// Google account for cross-device sync. The server-side session cookie
// is only set after a successful Google connection.
//
// `user` is always non-null — represents the current visitor. It carries:
//   - id:        local UUID (always present)
//   - email:     local@financeapp.local (default) or connected Google email
//   - name:      '本地用戶' (default) or Google profile name
//   - isLocal:   true if no Google connection yet
//   - googleSub: present if connected

import { useEffect, useState, useCallback, createContext, useContext } from 'react';
import { api } from '../api/client.js';
import { ensureSeeded, getMeta, setMeta, wipeLocalAccount } from './localDb.js';
import { flushSyncQueue } from './syncQueue.js';

const AuthContext = createContext({
  user: null, loading: true, clientId: null,
  signIn: async () => {}, signOut: async () => {}, deleteLocalAccount: async () => {},
  isLocal: true,
});

// Phase 151 (M-78): exported for tests so they can wrap a stub
// provider without spinning up the real AuthProvider (which would
// call /auth/me on mount and other server side effects).
export { AuthContext };

// Phase 201a: exported for testing. Validates the Google Identity
// Services response before we POST to /api/auth/google. The server
// returns 400 'missing_id_token' when the credential is empty, which
// is the visible symptom when:
//   - the Google Cloud OAuth client's Authorized JS Origins doesn't
//     include the current origin (so GIS returns an empty credential
//     after a successful popup)
//   - the user closed the popup without choosing an account
//   - FedCM / browser policy blocked the response
// Without this check, the user sees a confusing 400 in the Settings
// card; with it, they get an actionable message that points at the
// most likely cause. Exported so the test can exercise the failure
// paths without rendering the full <AuthProvider> (which needs IDB).
export function validateGoogleCredential(resp) {
  if (!resp || !resp.credential || typeof resp.credential !== 'string') {
    throw new Error(
      'Google 登入未回傳 credential，請確認 Google Cloud Console 的 ' +
      'OAuth client 已將目前的 origin 加入 Authorized JavaScript origins'
    );
  }
  return resp.credential;
}

let _gisLoading = null;
// Resolve when the GIS script is loaded. Returns the `google` global.
function loadGisScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (_gisLoading) return _gisLoading;
  _gisLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return _gisLoading;
}

// Cache the public config (googleClientId) for the session so we don't
// re-fetch on every component mount. Phase 201e: cache only on success;
// a previous fetch failure (e.g. transient CORS / network blip) must
// not poison the cache and lock the user out of the sign-in button.
let _configCache = null;
export function getConfig() {
  if (_configCache) return Promise.resolve(_configCache);
  return api.get('/config').then((c) => {
    if (c && typeof c === 'object') _configCache = c;
    return c;
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState(null);

  // Phase 201e: re-fetch /api/config on window focus. The previous
  // behavior was fetch-once-on-mount, so a transient /api/config
  // failure (or a stale service worker pre-201c that cached a 403
  // from Render's CORS reject) left clientId=null for the rest
  // of the tab's life. Window focus is cheap and catches both
  // "the user just enabled network" and "the SW finally updated
  // to the post-201c code". Keeps the existing module-level
  // _configCache so we don't re-fetch on every focus tick once
  // the config is good.
  const refreshClientId = useCallback(() => {
    getConfig().then((cfg) => {
      if (cfg?.googleClientId) {
        setClientId(cfg.googleClientId);
        if (typeof window !== 'undefined') {
          window.__GOOGLE_CLIENT_ID__ = cfg.googleClientId;
        }
        loadGisScript().catch((e) => {
          console.warn('[auth] GIS script load failed:', e?.message || e);
        });
      }
    }).catch((e) => {
      console.warn('[auth] /api/config fetch failed:', e?.message || e);
    });
  }, []);

  // On mount: ensure local DB is seeded (creates sentinel categories
  // + settings for new visitors), then build the local user object.
  // Optional Google connection state is read from the meta table.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Seed local DB first — must complete before anything reads
        // categories or settings.
        await ensureSeeded();

        const localUuid = await getMeta('local_user_uuid');
        const googleSub = await getMeta('connected_google_sub');
        const email = await getMeta('connected_user_email');

        // Only ask the server about the current session if the local
        // meta says we previously connected with Google. For fresh
        // local users, skip the network call entirely — there's no
        // session cookie to find, and the 401 response just clutters
        // the browser console with a red error.
        let serverMe = null;
        if (googleSub) {
          try {
            serverMe = await api.get('/auth/me');
          } catch {
            serverMe = null;
          }
        }

        if (cancelled) return;

        // Resolve user: prefer server-truth (cookie) > connected-google meta > local-only.
        if (serverMe) {
          // Server confirmed a Google session. Mirror it locally.
          // /auth/me returns {id, google_sub, email, name, picture}
          // since Phase 201d. We still keep the local meta's
          // googleSub as the source of truth on disk (idempotent
          // write).
          await setMeta('connected_google_sub', serverMe.google_sub);
          await setMeta('connected_user_email', serverMe.email);
          setUser({
            id: localUuid,
            email: serverMe.email,
            name: serverMe.name,
            isLocal: false,
            googleSub: serverMe.google_sub,
          });
          // Phase 35e: drain any pending sync-queue entries left over
          // from a previous offline session. Fire-and-forget so we
          // don't block the UI from rendering.
          flushSyncQueue().catch((e) =>
            console.warn('[auth] flushSyncQueue failed:', e?.message || e)
          );
        } else if (googleSub) {
          // Meta says we connected before but cookie is gone (cleared).
          // Stay in local-only mode until they reconnect.
          setUser({
            id: localUuid,
            email: email || 'local@financeapp.local',
            name: '本地用戶',
            isLocal: true,
            googleSub: null,
          });
        } else {
          // Pure local user — the default for new visitors.
          setUser({
            id: localUuid,
            email: 'local@financeapp.local',
            name: '本地用戶',
            isLocal: true,
            googleSub: null,
          });
        }

        // Fetch the public config in the background — non-blocking.
        // Phase 201e: log failures (was `.catch(() => {})` which made
        // /api/config fetch errors invisible). The "clientId stays
        // null" symptom reported by users (Settings card showing
        // 'Coming Soon' forever) was impossible to diagnose without
        // seeing the actual error.
        getConfig().then((cfg) => {
          if (cancelled) return;
          setClientId(cfg?.googleClientId || null);
          if (typeof window !== 'undefined') {
            window.__GOOGLE_CLIENT_ID__ = cfg?.googleClientId || null;
          }
          if (cfg?.googleClientId) loadGisScript().catch((e) => {
            console.warn('[auth] GIS script load failed:', e?.message || e);
          });
        }).catch((e) => {
          console.warn('[auth] /api/config fetch failed:', e?.message || e,
            '— sign-in button will stay disabled until next page load.');
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase 201e: window focus → refresh clientId. See refreshClientId
  // comment for the full rationale. Only attaches the listener in
  // the browser (jsdom test environments don't fire focus events).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('focus', refreshClientId);
    return () => window.removeEventListener('focus', refreshClientId);
  }, [refreshClientId]);

  const signIn = useCallback(async () => {
    if (!clientId) {
      throw new Error('Google 登入尚未設定 (GOOGLE_CLIENT_ID missing)');
    }
    const google = await loadGisScript();
    return new Promise((resolve, reject) => {
      google.accounts.id.initialize({
        client_id: clientId,
        callback: async (resp) => {
          // Phase 201a: validate the credential before POSTing. The
          // extracted helper is unit-tested; see auth.test.jsx.
          let idToken;
          try {
            idToken = validateGoogleCredential(resp);
          } catch (e) {
            return reject(e);
          }
          try {
            const me = await api.post('/auth/google', { idToken });
            // Mirror to local meta so reconnect on this device works
            // even if the cookie was cleared. /auth/google returns
            // {id, google_sub, email, name, picture} since Phase 201d.
            await setMeta('connected_google_sub', me.google_sub);
            await setMeta('connected_user_email', me.email);
            const localUuid = await getMeta('local_user_uuid');
            const localUser = {
              id: localUuid,
              email: me.email,
              name: me.name,
              isLocal: false,
              googleSub: me.google_sub,
            };
            setUser(localUser);
            // Phase 35e: drain pending sync queue after sign-in too.
            // This catches entries queued during the brief offline
            // window between previous session end and current sign-in.
            flushSyncQueue().catch((e) =>
              console.warn('[auth] flushSyncQueue failed:', e?.message || e)
            );
            resolve(localUser);
          } catch (e) {
            reject(e);
          }
        },
        cancel_on_tap_outside: true,
      });
      google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          reject(new Error('Google One Tap 不可用 — 請用按鈕登入'));
        }
      });
    });
  }, [clientId]);

  const signOut = useCallback(async () => {
    // Local-first: signing out means "disconnect from Google sync",
    // not "lock the app". Local data is preserved.
    try { await api.post('/auth/logout'); } catch {}
    await setMeta('connected_google_sub', null);
    await setMeta('connected_user_email', null);
    const localUuid = await getMeta('local_user_uuid');
    setUser({
      id: localUuid,
      email: 'local@financeapp.local',
      name: '本地用戶',
      isLocal: true,
      googleSub: null,
    });
  }, []);

  const isLocal = !!(user && user.isLocal);

  // Phase 46: Delete the entire local account. Wipes every IndexedDB
  // table including _meta (so local_user_uuid, connected_google_sub
  // are gone). Reloads the page so React re-mounts AuthProvider with a
  // fresh local user. Caller is responsible for confirming with the
  // user — this is destructive and irreversible.
  const deleteLocalAccount = useCallback(async () => {
    await wipeLocalAccount();
    // Phase 200: /signin route was removed; reload at / so the new
    // local user boots in a clean app shell. The Account binding
    // card on Settings is where the user goes to reconnect.
    if (typeof window !== 'undefined') window.location.href = '/';
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, clientId, signIn, signOut, deleteLocalAccount, isLocal, refreshClientId }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
